import { getDispensaryApiBaseUrl } from "./api-base";

export type OnboardingRecordDto = {
  id: string;
  step: string;
  completedAt: string | null;
  failedAt: string | null;
  failureReason: string | null;
  metadata: unknown;
  createdAt: string;
};

/** Ordered steps for dispensary onboarding (DEV-055). */
export const DISPENSARY_ONBOARDING_FLOW = [
  "kyb_verification",
  "bank_account_linking",
  "fee_schedule_agreement",
  "completed",
] as const;

export type DispensaryOnboardingStep = (typeof DISPENSARY_ONBOARDING_FLOW)[number];

export async function fetchDispensaryOnboarding(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
): Promise<{ records: OnboardingRecordDto[] }> {
  const token = await getToken();
  if (!token) throw new Error("not_signed_in");
  const base = getDispensaryApiBaseUrl();
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/onboarding`;
  const url = base ? `${base}${path}` : path;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`onboarding_list_http_${res.status}`);
  return (await res.json()) as { records: OnboardingRecordDto[] };
}

export async function completeDispensaryOnboarding(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  body: {
    step: DispensaryOnboardingStep;
    metadata?: Record<string, unknown>;
    plaidItemId?: string;
    plaidAccessToken?: string;
    bankAccountLast4?: string | null;
    bankName?: string | null;
  },
): Promise<{ record: OnboardingRecordDto }> {
  const token = await getToken();
  if (!token) throw new Error("not_signed_in");
  const base = getDispensaryApiBaseUrl();
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/onboarding/complete`;
  const url = base ? `${base}${path}` : path;
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`onboarding_complete_http_${res.status}:${t.slice(0, 200)}`);
  }
  return (await res.json()) as { record: OnboardingRecordDto };
}

export async function failDispensaryOnboarding(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  body: { step: DispensaryOnboardingStep; failureReason: string; metadata?: Record<string, unknown> },
): Promise<{ record: OnboardingRecordDto }> {
  const token = await getToken();
  if (!token) throw new Error("not_signed_in");
  const base = getDispensaryApiBaseUrl();
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/onboarding/fail`;
  const url = base ? `${base}${path}` : path;
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`onboarding_fail_http_${res.status}:${t.slice(0, 200)}`);
  }
  return (await res.json()) as { record: OnboardingRecordDto };
}

export function deriveDispensaryOnboardingStep(
  records: OnboardingRecordDto[],
  profileOnboardingCompletedAt: string | null,
): { done: true } | { done: false; step: DispensaryOnboardingStep; failedReason: string | null } {
  if (profileOnboardingCompletedAt) return { done: true };
  for (const step of DISPENSARY_ONBOARDING_FLOW) {
    const rec = records.find((r) => r.step === step);
    if (!rec?.completedAt) {
      return {
        done: false,
        step,
        failedReason: rec?.failedAt ? rec.failureReason : null,
      };
    }
  }
  return { done: true };
}
